import ieVersion from "ie-version";
import { pageView } from "./services/tracking";
import ErrorBoundary from "./components/error-boundary";

const shouldRedirectForIE = !!ieVersion.version;

if (shouldRedirectForIE) {
  if (process.env.PREVIEW) {
    pageView("/preview");
    document.location.href = "/preview.html";
  } else {
    pageView("/ie");
    document.location.href = "/ie.html";
  }
}

import _ from "lodash";
import React, { Component } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { DragDropContext } from "react-beautiful-dnd";
import AppRoutes from "./routes";
import SplashScreen from "./splash-screen";
import PreviewPage from "./preview";
import { indexInitialData, useStubData } from "./drawer-items";
import preloadFilingCabinet from "./preload-filing-cabinet";
import { soundReady, preloadSoundSprite } from "./sound";
import store from "./store";
import { sfxToLoad } from "./drawer-sfx";
import NotSupported from "./not-supported";
import fakeSubscription from "./fake-subscription";
import { isIPad, isIPhone, isMobile } from "./platform";
import { changePlaylistItemsOrder } from "./actions/playlists";
import { updateEntries } from "./actions/entries";
import { loadFreeTracks } from "./actions/tracks";
import {
  hasAuth,
  isPaying,
  reloadPaymentProcess,
  getInitialData,
  setReferralFreePass,
  getDrawerData,
  generateUuid,
  getOrastreamUuid,
  setOrastreamUuid,
  getNewspaperData,
  createJWT,
  setAccessToken,
  getAccessToken,
} from "./services/api";
import { fetchInitialEntries, fetchExclusiveEntries } from "./services/fetch";
import { isFreeUser } from "./user";
import "./ios-hacks";
import styles from "../scss/main.scss";
import { pathInResponsiveViews } from "./route-utils";

console.log("NODE_ENV =", process.env.NODE_ENV);
console.log("CMS_ENV =", process.env.CMS_ENV);
console.log("IE VERSION = ", ieVersion.version);
console.log("FAKING SUBSCRIPTION SERVICES =", fakeSubscription);

let soundInitialized = false;
let soundLoaded = false;
let imagesLoaded = false;
let initialLoadComplete = false;
let isEmbed = false;

if (isIPad || isIPhone) {
  soundInitialized = true;
  soundLoaded = true;
}

let imagesPromise, soundReadyPromise, soundLoadedPromise, isYouTubeReferral;
const { pathname } = document.location;
const routeName = pathname.split("/")[1];
const forbiddenRoutes = ["times-contrarian"];
const requestDrawer = !forbiddenRoutes?.includes(routeName);

class App extends Component {
  constructor(props) {
    super(props);

    this.getLoadState = this.getLoadState.bind(this);
    this.checkLoad = this.checkLoad.bind(this);
    this.checkAuth = this.checkAuth.bind(this);

    this.state = {
      ...this.stateFromStore(store.getState()),
      showingAuth: false,
      underMaintenance: false,
      imagesLoaded: false,
      soundInitialized: false,
      soundLoaded: false,
    };
    this.onDragEnd = this.onDragEnd.bind(this);
  }

  startLoading(pageLocation) {
    let documentReferrer = document.referrer;
    isYouTubeReferral =
      documentReferrer.includes("youtube") || pageLocation.includes("youtube");

    if (ieVersion.version && ieVersion.version < 11) {
      console.log("SKIPPING PRELOADING FOR IE");
      this.setState({
        soundInitialized: true,
        soundLoaded: true,
        imagesLoaded: true,
      });

      imagesPromise =
        soundReadyPromise =
        soundLoadedPromise =
          Promise.resolve(true);
      return;
    }
    if (requestDrawer) {
      imagesPromise = preloadFilingCabinet().then(() => {});
    } else {
      imagesPromise = Promise.resolve(true);
    }
    this.setState({ imagesLoaded: true });

    if (requestDrawer) {
      soundReadyPromise = soundReady().then(() => {
        this.setState({ soundInitialized: true });
      });
      soundLoadedPromise = soundReadyPromise
        .then(() => {
          let toLoad = sfxToLoad().concat([
            ["drawer-open", "/sounds/filecabinet_opening_1sec.mp3"],
          ]);
          return Promise.all(
            toLoad.map((pair) => preloadSoundSprite.apply(null, pair))
          );
        })
        .then(() => {
          this.setState({ soundLoaded: true });
        });
    } else {
      soundReadyPromise = Promise.resolve(true);
      soundLoadedPromise = Promise.resolve(true);
    }
  }

  getLoadState() {
    const elms = [
      "tracksLoaded",
      // 'albumsLoaded',
      "imagesLoaded",
      // 'soundInitialized',
      "soundLoaded",
    ];

    const loaded = _.size(_.pickBy(_.pick(this.state, elms), _.identity));

    return loaded ? Math.floor(100 / _.size(elms)) * (loaded + 1) : -1;
  }

  stateFromStore(state) {
    const isFirstTime = !this.state;
    return {
      tracksLoaded: useStubData ? true : state.tracks.loaded,
      albumsLoaded: useStubData ? true : state.albums.loaded,
      commonValuesLoaded: useStubData ? true : state.commonValues.loaded,
      imagesLoaded: isFirstTime ? imagesLoaded : this.state.imagesLoaded,
      soundInitialized: isFirstTime
        ? soundInitialized
        : this.state.soundInitialized,
      soundLoaded: isFirstTime ? soundLoaded : this.state.soundLoaded,
    };
  }

  isReadyForDrawer(state = this.state) {
    let {
      tracksLoaded,
      albumsLoaded,
      imagesLoaded,
      soundInitialized,
      soundLoaded,
    } = state;
    return (
      soundInitialized &&
      soundLoaded &&
      imagesLoaded &&
      tracksLoaded &&
      albumsLoaded
    );
  }

  appIsLoaded(state = this.state) {
    const { pathname } = document.location;
    const routeName = pathname.split("/")[1];
    const dontWaitForDrawer = [
      "",
      "menu",
      "news",
      "faq",
      "contact",
      "audio-setup",
      "drawer",
      "timeline",
      "album",
      "playlist",
      "movie-night",
      "embed",
      "times-contrarian",
      "welcome",
    ];

    let { commonValuesLoaded } = state;
    return (
      commonValuesLoaded &&
      (dontWaitForDrawer.indexOf(routeName) !== -1 ||
        this.isReadyForDrawer(state))
    );
  }

  // Añadimos estos métodos que estaban referenciados pero no definidos
  checkLoad = () => {
    if (initialLoadComplete) return;
    let state = store.getState();
    let newstate = this.stateFromStore(state);

    if (this.appIsLoaded(newstate) && this.isReadyForDrawer(newstate)) {
      initialLoadComplete = true;
      indexInitialData(state);
    }
    if (!_.isEqual(this.state, newstate)) {
      this.setState(newstate);
    }
  };

  checkAuth = () => {
    if (initialLoadComplete) return;
    let newStatus = store.getState().status;
    if (newStatus === this.status && this.status === "failed") return;
    this.status = newStatus;
    console.log("FETCH STATUS = ", this.status);
    console.log(newStatus);
    if (this.status !== "failed") {
      this.checkLoad();
    }
  };

  async componentDidMount() {
    const pageLocation = window.location.href;
    isEmbed = pageLocation.includes("embed");
    console.log(
      "%c Is Embed route ? ",
      "color:red; background:black;",
      isEmbed
    );

    this.setState({
      imagesLoaded: false,
      soundInitialized: false,
      soundLoaded: false,
    });

    this.startLoading(pageLocation);

    setTimeout(() => {
      store.subscribe(this.checkLoad);
      store.subscribe(this.checkAuth);
    }, 100);

    if (isPaying()) {
      setTimeout(() => {
        if (isPaying()) reloadPaymentProcess(0, true);
      }, 5000);
    }

    soundLoadedPromise.then(this.checkLoad);
    imagesPromise.then(this.checkLoad);
    store.subscribe(this.checkLoad);

    this.status = store.getState().status;
    store.subscribe(this.checkAuth);

    Promise.all([
      getInitialData(),
      isEmbed ? Promise.resolve() : getNewspaperData(),
    ]).then(([data, newspaperData]) => {
      this.setState({ underMaintenance: data.appStatus == "true" });
      const { commonValues = { freeTracks: {} } } = data;
      let parsedTracks = [];
      if (typeof commonValues.freeTracks === "object") {
        for (let TRACK_ID in commonValues.freeTracks) {
          parsedTracks.push(commonValues.freeTracks[TRACK_ID]);
        }
      }
      loadFreeTracks(parsedTracks);
      updateEntries(data);
      updateEntries(newspaperData);
      if (!getAccessToken()) createJWT().then(setAccessToken);
    });

    if (!isEmbed && requestDrawer) {
      getDrawerData().then((drawerData) => {
        updateEntries(drawerData);
      });
    }

    fetchInitialEntries();

    if (!getOrastreamUuid()) {
      generateUuid().then((token) => {
        if (token.uuid) setOrastreamUuid(token.uuid);
      });
    }
    if (hasAuth() && !isEmbed) {
      fetchExclusiveEntries().then(() => {
        if (isYouTubeReferral && isFreeUser()) setReferralFreePass();
      });
    } else if (!isEmbed) {
      if (isYouTubeReferral) setReferralFreePass();
    }
  }

  onDragEnd(result) {
    if (result.destination) {
      store.dispatch(changePlaylistItemsOrder(result));
    }
  }

  render() {
    console.log("App Loaded:", this.appIsLoaded());
    console.log("State:", this.state);
    if (this.state.underMaintenance) {
      return <div className="underMaintenance" />;
    }
    if (this.appIsLoaded()) {
      return (
        <ErrorBoundary>
          <DragDropContext onDragEnd={this.onDragEnd}>
            <BrowserRouter>
              <AppRoutes store={store} />
            </BrowserRouter>
          </DragDropContext>
        </ErrorBoundary>
      );
    } else {
      return <SplashScreen loadState={this.getLoadState()} isEmbed={isEmbed} />;
    }
  }
}

if (shouldRedirectForIE) {
} else if (process.env.PREVIEW) {
  pageView("/");
  ReactDOM.render(<PreviewPage />, document.getElementById("app"));
} else if (isMobile && !isIPad && !pathInResponsiveViews()) {
  pageView("/mobile-not-supported");
  ReactDOM.render(<NotSupported />, document.getElementById("app"));
} else {
  pageView("/start");
  ReactDOM.render(<App />, document.getElementById("app"));
}
